import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { DashboardLayout } from './Dashboard';
import { Eye, ArrowsClockwise, Trash, Warning, CheckCircle, Clock, Plus, ClockClockwise } from '@phosphor-icons/react';
import { HTTP_STATUS } from '@/lib/constants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ChangesBadge = ({ monitor }) => {
  const { t } = useTranslation();
  if (monitor.has_changes) {
    return (
      <div className="flex items-center gap-1 px-2 py-1 bg-[#F59E0B]/20 rounded-full">
        <Warning size={14} weight="bold" className="text-[#F59E0B]" />
        <span className="text-xs text-[#F59E0B] font-medium">{t('mon.changes_detected')}</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1 px-2 py-1 bg-[#10B981]/20 rounded-full">
      <CheckCircle size={14} weight="bold" className="text-[#10B981]" />
      <span className="text-xs text-[#10B981] font-medium">{t('mon.stable')}</span>
    </div>
  );
};

const ChangesDetail = ({ changes }) => {
  const { t } = useTranslation();
  if (!changes || Object.keys(changes).length === 0) return null;
  return (
    <div className="mt-3 p-3 bg-[#0A0A0A] border border-[#F59E0B]/30 rounded-lg space-y-2">
      <p className="text-xs uppercase tracking-[0.2em] text-[#F59E0B] font-medium">{t('mon.changes')}</p>
      {Object.entries(changes).map(([field, diff]) => (
        <div key={field} className="text-sm">
          <p className="text-white/80 font-medium capitalize">{field}:</p>
          {diff.old !== undefined && (
            <p className="text-white/60 text-xs">
              <span className="text-[#EF4444]">- {diff.old}</span>
            </p>
          )}
          {diff.new !== undefined && (
            <p className="text-white/60 text-xs">
              <span className="text-[#10B981]">+ {diff.new}</span>
            </p>
          )}
          {diff.added?.length > 0 && (
            <p className="text-xs text-[#10B981]">+ {diff.added.join(', ')}</p>
          )}
          {diff.removed?.length > 0 && (
            <p className="text-xs text-[#EF4444]">- {diff.removed.join(', ')}</p>
          )}
        </div>
      ))}
    </div>
  );
};

const MonitorCard = ({ monitor, checking, onCheck, onDelete, onViewHistory, lastChange }) => {
  const { t, i18n } = useTranslation();
  return (
  <div
    data-testid={`monitor-${monitor.monitor_id}`}
    className="p-6 bg-[#121212] border border-white/10 rounded-xl hover:border-[#2563EB]/50 transition-all"
  >
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 mb-2">
          <h3 className="text-xl font-[Outfit] font-bold truncate">{monitor.label}</h3>
          <ChangesBadge monitor={monitor} />
        </div>
        <a
          href={monitor.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-[#2563EB] hover:text-[#3B82F6] break-all"
        >
          {monitor.url}
        </a>
        <div className="flex items-center gap-4 mt-3 text-xs text-white/40">
          <span className="flex items-center gap-1">
            <Clock size={12} weight="bold" />
            {t('mon.last_check')} {new Date(monitor.last_checked_at).toLocaleString(i18n.language === 'pt' ? 'pt-PT' : 'en-US')}
          </span>
          <span>{t('mon.change_count', { n: monitor.change_count })}</span>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={() => onViewHistory(monitor.monitor_id)}
          data-testid={`history-btn-${monitor.monitor_id}`}
          className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 hover:text-white rounded-lg text-sm transition-all"
        >
          <ClockClockwise size={16} weight="bold" />
          {t('mon.history')}
        </button>
        <button
          onClick={() => onCheck(monitor.monitor_id)}
          data-testid={`check-btn-${monitor.monitor_id}`}
          disabled={checking}
          className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 hover:text-white rounded-lg text-sm transition-all disabled:opacity-50"
        >
          <ArrowsClockwise size={16} weight="bold" className={checking ? 'animate-spin' : ''} />
          {t('mon.check')}
        </button>
        <button
          onClick={() => onDelete(monitor.monitor_id)}
          data-testid={`delete-btn-${monitor.monitor_id}`}
          className="p-2 bg-white/10 hover:bg-[#EF4444]/20 hover:text-[#EF4444] rounded-lg transition-all"
        >
          <Trash size={16} weight="bold" />
        </button>
      </div>
    </div>
    <ChangesDetail changes={lastChange} />
  </div>
  );
};

const EmptyMonitorState = () => {
  const { t } = useTranslation();
  return (
  <div className="text-center py-16">
    <div className="w-20 h-20 bg-[#2563EB]/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
      <Eye size={40} weight="bold" className="text-[#2563EB]" />
    </div>
    <h3 className="text-2xl font-[Outfit] font-bold mb-3">{t('mon.empty_title')}</h3>
    <p className="text-white/60 mb-6">{t('mon.empty_sub')}</p>
  </div>
  );
};

const LoadingSpinner = () => (
  <div className="flex items-center justify-center py-16">
    <div className="w-16 h-16 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin"></div>
  </div>
);

const AddMonitorForm = ({ newUrl, setNewUrl, newLabel, setNewLabel, adding, onSubmit }) => {
  const { t } = useTranslation();
  return (
  <form onSubmit={onSubmit} className="mb-8 p-6 bg-[#121212] border border-white/10 rounded-xl space-y-4">
    <div>
      <label className="block text-sm font-medium text-white/80 mb-2">{t('mon.url_label')}</label>
      <input
        type="url"
        data-testid="monitor-url-input"
        value={newUrl}
        onChange={(e) => setNewUrl(e.target.value)}
        placeholder="https://concorrente.com/produto"
        required
        className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:border-[#2563EB]"
      />
    </div>
    <div>
      <label className="block text-sm font-medium text-white/80 mb-2">{t('mon.label_label')}</label>
      <input
        type="text"
        data-testid="monitor-label-input"
        value={newLabel}
        onChange={(e) => setNewLabel(e.target.value)}
        placeholder={t('mon.label_ph')}
        className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:border-[#2563EB]"
      />
    </div>
    <button
      type="submit"
      data-testid="submit-monitor"
      disabled={adding}
      className="px-6 py-3 bg-[#2563EB] hover:bg-[#3B82F6] rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {adding ? t('mon.adding') : t('mon.add_btn')}
    </button>
  </form>
  );
};

const Monitoring = ({ user }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [monitors, setMonitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [adding, setAdding] = useState(false);
  const [checking, setChecking] = useState({});
  const [lastChanges, setLastChanges] = useState({});

  const loadMonitors = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/monitor`, { withCredentials: true });
      setMonitors(response.data);
    } catch {
      // Silently fail - empty state shows
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMonitors();
  }, [loadMonitors]);

  const handleAdd = useCallback(async (e) => {
    e.preventDefault();
    if (!newUrl.trim()) return;
    setAdding(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/monitor`,
        { url: newUrl.trim(), label: newLabel.trim() || undefined },
        { withCredentials: true }
      );
      setNewUrl('');
      setNewLabel('');
      setShowAdd(false);
      await loadMonitors();
    } catch (err) {
      const status = err.response?.status;
      if (status === HTTP_STATUS.CONFLICT) {
        toast.error(t('spy.err_conflict'));
      } else if (status === 402) {
        toast.error(t('spy.err_limit'), {
          description: t('spy.err_limit_desc'),
          action: { label: t('spy.view_plans'), onClick: () => navigate('/pricing') }
        });
      } else {
        toast.error(t('mon.err_add'));
      }
    } finally {
      setAdding(false);
    }
  }, [newUrl, newLabel, loadMonitors, navigate, t]);

  const handleCheck = useCallback(async (monitorId) => {
    setChecking((prev) => ({ ...prev, [monitorId]: true }));
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/monitor/${monitorId}/check`,
        {},
        { withCredentials: true }
      );
      setLastChanges((prev) => ({ ...prev, [monitorId]: response.data.changes }));
      await loadMonitors();
    } catch {
      // Check failed - keep previous state
    } finally {
      setChecking((prev) => ({ ...prev, [monitorId]: false }));
    }
  }, [loadMonitors]);

  const handleDelete = useCallback(async (monitorId) => {
    toast(t('mon.confirm_remove'), {
      action: {
        label: t('mon.remove'),
        onClick: async () => {
          try {
            await axios.delete(`${BACKEND_URL}/api/monitor/${monitorId}`, { withCredentials: true });
            toast.success(t('mon.removed'));
            await loadMonitors();
          } catch {
            toast.error(t('mon.remove_fail'));
          }
        }
      }
    });
  }, [loadMonitors, t]);

  const handleViewHistory = useCallback((monitorId) => {
    navigate(`/monitoring/${monitorId}`);
  }, [navigate]);

  const renderMonitorList = () => {
    if (loading) return <LoadingSpinner />;
    if (monitors.length === 0) return <EmptyMonitorState />;
    return (
      <div className="space-y-4">
        {monitors.map((monitor) => (
          <MonitorCard
            key={monitor.monitor_id}
            monitor={monitor}
            checking={checking[monitor.monitor_id]}
            onCheck={handleCheck}
            onDelete={handleDelete}
            onViewHistory={handleViewHistory}
            lastChange={lastChanges[monitor.monitor_id]}
          />
        ))}
      </div>
    );
  };

  return (
    <DashboardLayout user={user} activePage="/monitoring">
      <div className="p-6" data-testid="monitoring-page">
        <div className="mb-8 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-[#2563EB]/20 rounded-xl flex items-center justify-center">
                <Eye size={28} weight="bold" className="text-[#2563EB]" />
              </div>
              <h1 className="text-4xl font-[Outfit] font-bold tracking-tight">{t('mon.title')}</h1>
            </div>
            <p className="text-white/60">{t('mon.subtitle')}</p>
          </div>
          <button
            onClick={() => setShowAdd((v) => !v)}
            data-testid="toggle-add-monitor"
            className="flex items-center gap-2 px-4 py-2 bg-[#2563EB] hover:bg-[#3B82F6] rounded-lg font-medium transition-all"
          >
            <Plus size={20} weight="bold" />
            {showAdd ? t('mon.cancel') : t('mon.new_monitor')}
          </button>
        </div>

        {showAdd && (
          <AddMonitorForm
            newUrl={newUrl}
            setNewUrl={setNewUrl}
            newLabel={newLabel}
            setNewLabel={setNewLabel}
            adding={adding}
            onSubmit={handleAdd}
          />
        )}

        {renderMonitorList()}
      </div>
    </DashboardLayout>
  );
};

export default Monitoring;

